import { useLocation } from 'react-router-dom';
import React, { useState, useRef, useEffect } from 'react';
import DrawingCanvas from '../Canvas/DrawingCanvas'; // Import your actual DrawingCanvas component
import BubbleCanvas from '../Canvas/BubbleCanvas'; // Import your BubbleCanvas component
import ShareStoryModal from '../Canvas/ShareStoryModal';
import { useTool } from '../../context/ToolContext'; // Import your actual contexts
import { useProject } from '../../context/ProjectContext';
import { useAuth } from '../../../../../contexts/AuthContext';
import { StoryService } from '../../../../../services/storyService';

// Tool modes
const TOOL_MODES = {
  SELECT: 'select',
  DRAW: 'draw',
  ERASER: 'eraser',
  LINE: 'line',
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  PANEL: 'panel',
  TEXT: 'text',
  BUBBLE: 'bubble'
};

const SketchedCanvasInterface: React.FC = () => {
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [activeToolTab, setActiveToolTab] = useState('draw');
  const [showPreview, setShowPreview] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { currentUser } = useAuth();
  const [storyId, setStoryId] = useState<string | null>(null);
  const location = useLocation();

  const theme = {
    primary: '#FF6B35',
    secondary: '#9B59B6',
    accent: '#F39C12',
    success: '#2ECC71',
    pink: '#E91E63',
    blue: '#3498DB'
  };

  // Use your actual contexts
  const toolContext = useTool();
  const projectContext = useProject();
  
  const {
    currentMode,
    selectedColor,
    brushSize,
    brushType,
    eraserSize,
    selectedElement,
    selectElement,
    clearSelection,
    addToUndoStack,
    hasUndo,
    hasRedo
  } = toolContext;

  const {
    currentPageIndex,
    pages,
    updatePage,
    projectName,
    author
  } = projectContext;

  // Use the correct method names from your ProjectContext
  const handleModeChange = (mode: string) => {
    toolContext.setMode(mode as any);
  };

  const handleColorChange = (color: string) => {
    toolContext.setColor(color);
  };

  const handleBrushSizeChange = (size: number) => {
    toolContext.setBrushSize(size);
  };

  const handleAddNewPage = () => {
    projectContext.addPage();
  };

  const handleNavigateToPage = (index: number) => {
    projectContext.setCurrentPage(index);
  };

  const handleDeletePage = (index: number) => {
    projectContext.deletePage(index);
  };

  const handleUndo = () => {
    const undoAction = toolContext.undo();
    
    // Handle the undo action based on its type
    if (undoAction) {
      switch (undoAction.type) {
        case 'ADD_BUBBLE':
        case 'DELETE_BUBBLE':
        case 'UPDATE_BUBBLE':
          // The ToolContext has the action, but we need to actually undo it in ProjectContext
          const currentPage = pages[undoAction.pageIndex];
          if (currentPage) {
            // This is a simplified undo - in a full implementation you'd need more sophisticated undo logic
            console.log('Undo action:', undoAction.type);
          }
          break;
        
        default:
          console.log('Unknown undo action:', undoAction.type);
      }
    }
  };

  const handleRedo = () => {
    const redoAction = toolContext.redo();
    
    // Handle the redo action based on its type
    if (redoAction) {
      switch (redoAction.type) {
        
        default:
          console.log('Unknown redo action:', redoAction.type);
      }
    }
  };

  const handleSaveProject = async () => {
    if (!currentUser) {
      alert('Please sign in to save your story');
      return;
    }
  
    try {
      const storyData = {
        title: projectName || 'Untitled Graphic Novel',
        type: 'graphic-novel' as const,
        content: {
          pages: pages, // Your current pages data
          // Include any other canvas data you need
        },
        description: `A graphic novel with ${pages.length} pages`,
        pageCount: pages.length,
        author: currentUser.displayName || 'Anonymous'
      };
  
      if (storyId) {
        // Update existing story
        await StoryService.updateStory(currentUser.uid, storyId, storyData);
      } else {
        // Create new story
        const newStoryId = await StoryService.createStory(currentUser.uid, storyData);
        setStoryId(newStoryId);
      }
      
      alert('Story saved successfully!');
    } catch (error) {
      console.error('Error saving story:', error);
      alert('Failed to save story. Please try again.');
    }
  };

  const colorPalettes = [
    {
      name: 'Comic Classic',
      colors: [theme.primary, theme.secondary, theme.accent, theme.success, theme.pink, theme.blue, '#000000', '#FFFFFF', '#E74C3C', '#95A5A6']
    },
    {
      name: 'Superhero',
      colors: ['#DC143C', '#FFD700', '#4169E1', '#32CD32', '#FF4500', '#9932CC', '#000000', '#C0C0C0', '#FF1493', '#00CED1']
    },
    {
      name: 'Manga',
      colors: ['#000000', '#FFFFFF', '#808080', '#D3D3D3', '#FF69B4', '#87CEEB', '#98FB98', '#F0E68C', '#DDA0DD', '#F5DEB3']
    },
    {
      name: 'Fantasy',
      colors: ['#8B4513', '#228B22', '#4B0082', '#B8860B', '#DC143C', '#2F4F4F', '#FF6347', '#9ACD32', '#DA70D6', '#20B2AA']
    }
  ];

  const [activeColorPalette, setActiveColorPalette] = useState(0);

  const drawingTools = [
    { mode: TOOL_MODES.DRAW, icon: '🖌️', name: 'Brush', color: theme.primary },
    { mode: TOOL_MODES.ERASER, icon: '🧽', name: 'Eraser', color: theme.pink },
    { mode: TOOL_MODES.LINE, icon: '📏', name: 'Line', color: theme.blue },
    { mode: TOOL_MODES.RECTANGLE, icon: '⬜', name: 'Rectangle', color: theme.success },
    { mode: TOOL_MODES.CIRCLE, icon: '⭕', name: 'Circle', color: theme.accent },
    { mode: TOOL_MODES.SELECT, icon: '👆', name: 'Select', color: theme.secondary }
  ];

  const comicTools = [
    { mode: TOOL_MODES.BUBBLE, icon: '💬', name: 'Speech', color: theme.pink },
    { mode: TOOL_MODES.TEXT, icon: '🔤', name: 'Text', color: theme.accent }
  ];

  // Story loading useEffect - can go here
useEffect(() => {
    const loadStory = async () => {
      const state = location.state;
      if (state?.storyId && currentUser) {
        try {
          const story = await StoryService.getStory(currentUser.uid, state.storyId);
          if (story && story.content) {
            setStoryId(story.id || null);
            // Load story content into your editor
            if (story.content.pages) {
              // You'll need to adapt this to your actual page structure
              updatePage(0, story.content.pages[0] || {});
            }
          }
        } catch (error) {
          console.error('Error loading story:', error);
        }
      }
    };
  
    loadStory();
  }, [location.state, currentUser]);
  
  // Mobile check useEffect - existing one
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setLeftPanelCollapsed(true);
        setRightPanelCollapsed(true);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setLeftPanelCollapsed(true);
        setRightPanelCollapsed(true);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div style={{
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.primary} 50%, ${theme.accent} 100%)`,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderRadius: '0 0 25px 25px',
        padding: '20px 25px',
        marginBottom: '20px',
        boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
        border: `4px solid ${theme.primary}`,
        borderTop: 'none',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: '10%', left: '8%', width: '30px', height: '30px',
          background: theme.pink, borderRadius: '50%', opacity: 0.15
        }} />
        <div style={{
          position: 'absolute', bottom: '15%', right: '10%', width: '25px', height: '25px',
          background: theme.success, borderRadius: '50%', opacity: 0.2
        }} />
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
              borderRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
              fontSize: '28px'
            }}>
              🎨
            </div>
            
            <div>
              <h1 style={{
                fontSize: isMobile ? '1.8rem' : '2.2rem',
                fontWeight: '900',
                margin: 0,
                background: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {projectName}
              </h1>
              <p style={{
                fontSize: '1rem',
                color: '#666',
                fontWeight: '600',
                margin: 0
              }}>
                {author ? `by ${author}` : 'Create amazing comics and graphic novels!'}
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowPreview(!showPreview)}
              style={{
                background: `linear-gradient(135deg, ${theme.blue}, ${theme.secondary})`,
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                boxShadow: `0 8px 25px ${theme.blue}40`
              }}
            >
              📖 {showPreview ? 'Edit Mode' : 'Preview'}
            </button>
            <button
  onClick={handleSaveProject}
  style={{
    background: `linear-gradient(135deg, ${theme.success}, ${theme.accent})`,
    color: 'white',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '700',
    boxShadow: `0 8px 25px ${theme.success}40`
  }}
>
  💾 Save to Library
</button>
            
            <button
              onClick={() => setShowShareModal(true)}
              style={{
                background: `linear-gradient(135deg, ${theme.blue}, ${theme.pink})`,
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                boxShadow: `0 8px 25px ${theme.blue}40`
              }}
            >
              🌟 Share Story
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        gap: '20px', 
        padding: '0 20px 20px',
        overflow: 'hidden' 
      }}>
        {/* Left Panel - Tools */}
        {(!isMobile || !leftPanelCollapsed) && (
          <div style={{
            width: isMobile ? '100%' : leftPanelCollapsed ? '60px' : '320px',
            background: 'white',
            borderRadius: '20px',
            padding: leftPanelCollapsed ? '20px 15px' : '25px',
            boxShadow: '0 12px 35px rgba(0,0,0,0.1)',
            border: `3px solid ${theme.primary}`,
            overflowY: 'auto',
            transition: 'width 0.3s ease',
            position: 'relative'
          }}>
            <button
              onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '15px',
                background: theme.primary,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                zIndex: 10,
                display: isMobile ? 'none' : 'block'
              }}
            >
              {leftPanelCollapsed ? '→' : '←'}
            </button>

            {!leftPanelCollapsed && (
              <div style={{ 
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Tool Tabs */}
                <div style={{ marginBottom: '25px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {[
                      { id: 'draw', label: 'Drawing Tools', icon: '🖌️' },
                      { id: 'comic', label: 'Comic Tools', icon: '💬' },
                      { id: 'colors', label: 'Colors', icon: '🎨' },
                      { id: 'effects', label: 'Effects', icon: '✨' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveToolTab(tab.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px',
                          borderRadius: '12px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '700',
                          background: activeToolTab === tab.id 
                            ? `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` 
                            : 'transparent',
                          color: activeToolTab === tab.id ? 'white' : '#666',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>{tab.icon}</span>
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tool Content Area */}
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  paddingRight: '10px'
                }}>
                  {/* Drawing Tools */}
                  {activeToolTab === 'draw' && (
                    <div>
                      <h3 style={{
                        margin: '0 0 20px 0',
                        fontSize: '1.2rem',
                        fontWeight: '800',
                        background: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        🖌️ Drawing Tools
                      </h3>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '25px' }}>
                        {drawingTools.map(tool => (
                          <button
                            key={tool.mode}
                            onClick={() => handleModeChange(tool.mode)}
                            style={{
                              padding: '16px',
                              borderRadius: '12px',
                              border: `3px solid ${currentMode === tool.mode ? tool.color : '#ddd'}`,
                              background: currentMode === tool.mode 
                                ? `${tool.color}10` 
                                : 'white',
                              cursor: 'pointer',
                              textAlign: 'center',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>{tool.icon}</div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#333' }}>{tool.name}</div>
                          </button>
                        ))}
                      </div>

                      {/* Brush Settings */}
                      <div>
                        <h4 style={{
                          margin: '0 0 15px 0',
                          fontSize: '1rem',
                          fontWeight: '700',
                          color: theme.accent
                        }}>
                          ⚙️ Brush Settings
                        </h4>
                        
                        <div style={{ marginBottom: '15px' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>
                            Size: {brushSize}px
                          </label>
                          <input 
                            type="range" 
                            min="1" 
                            max="50" 
                            value={brushSize} 
                            onChange={(e) => handleBrushSizeChange(parseInt(e.target.value))}
                            style={{
                              width: '100%',
                              height: '6px',
                              borderRadius: '3px',
                              background: `linear-gradient(to right, ${theme.primary}, ${theme.secondary})`,
                              outline: 'none',
                              cursor: 'pointer'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Comic Tools */}
                  {activeToolTab === 'comic' && (
                    <div>
                      <h3 style={{
                        margin: '0 0 20px 0',
                        fontSize: '1.2rem',
                        fontWeight: '800',
                        background: `linear-gradient(45deg, ${theme.pink}, ${theme.accent})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        💬 Comic Tools
                      </h3>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '20px' }}>
                        {comicTools.map(tool => (
                          <button
                            key={tool.mode}
                            onClick={() => handleModeChange(tool.mode)}
                            style={{
                              padding: '16px',
                              borderRadius: '12px',
                              border: `3px solid ${currentMode === tool.mode ? tool.color : '#ddd'}`,
                              background: currentMode === tool.mode 
                                ? `${tool.color}10` 
                                : 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <div style={{ fontSize: '24px' }}>{tool.icon}</div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#333' }}>{tool.name}</div>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {tool.mode === TOOL_MODES.BUBBLE && 'Add speech bubbles'}
                                {tool.mode === TOOL_MODES.TEXT && 'Add text and captions'}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Bubble Type Selection */}
                      {currentMode === TOOL_MODES.BUBBLE && (
                        <div style={{
                          background: `${theme.pink}10`,
                          border: `3px solid ${theme.pink}30`,
                          borderRadius: '15px',
                          padding: '15px'
                        }}>
                          <h4 style={{
                            margin: '0 0 12px 0',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: theme.pink
                          }}>
                            Speech Bubble Types
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                            {[
                              { type: 'speech', icon: '💬', name: 'Speech' },
                              { type: 'thought', icon: '💭', name: 'Thought' },
                              { type: 'narration', icon: '📖', name: 'Narration' },
                              { type: 'shout', icon: '💥', name: 'Shout' },
                              { type: 'whisper', icon: '🤫', name: 'Whisper' }
                            ].map(bubbleTypeOption => (
                              <button
                                key={bubbleTypeOption.type}
                                onClick={() => toolContext.setBubbleType(bubbleTypeOption.type as any)}
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '8px',
                                  border: `2px solid ${toolContext.bubbleType === bubbleTypeOption.type ? theme.pink : '#ddd'}`,
                                  background: toolContext.bubbleType === bubbleTypeOption.type 
                                    ? `${theme.pink}20` 
                                    : 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}
                              >
                                <span style={{ fontSize: '16px' }}>{bubbleTypeOption.icon}</span>
                                <span>{bubbleTypeOption.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Font Controls for Text Mode */}
                      {(currentMode === TOOL_MODES.TEXT || currentMode === TOOL_MODES.BUBBLE) && (
                        <div style={{
                          background: `${theme.accent}10`,
                          border: `3px solid ${theme.accent}30`,
                          borderRadius: '15px',
                          padding: '15px',
                          marginTop: '15px'
                        }}>
                          <h4 style={{
                            margin: '0 0 12px 0',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: theme.accent
                          }}>
                            Text Settings
                          </h4>
                          
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '6px' }}>
                              Font Size: {toolContext.fontSize}px
                            </label>
                            <input 
                              type="range" 
                              min="8" 
                              max="48" 
                              value={toolContext.fontSize} 
                              onChange={(e) => toolContext.setFontSize(parseInt(e.target.value))}
                              style={{
                                width: '100%',
                                height: '4px',
                                borderRadius: '2px',
                                background: `linear-gradient(to right, ${theme.accent}, ${theme.primary})`,
                                outline: 'none',
                                cursor: 'pointer'
                              }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '6px' }}>
                              Font Family
                            </label>
                            <select 
                              value={toolContext.fontFamily}
                              onChange={(e) => toolContext.setFontFamily(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '6px',
                                border: '2px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '11px',
                                background: 'white'
                              }}
                            >
                              <option value="Comic Sans MS, cursive">Comic Sans MS</option>
                              <option value="Arial, sans-serif">Arial</option>
                              <option value="Times New Roman, serif">Times New Roman</option>
                              <option value="Courier New, monospace">Courier New</option>
                              <option value="Impact, sans-serif">Impact</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Colors */}
                  {activeToolTab === 'colors' && (
                    <div>
                      <h3 style={{
                        margin: '0 0 20px 0',
                        fontSize: '1.2rem',
                        fontWeight: '800',
                        background: `linear-gradient(45deg, ${theme.accent}, ${theme.success})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        🎨 Color Palettes
                      </h3>
                      
                      <div style={{ display: 'flex', gap: '4px', marginBottom: '15px', flexWrap: 'wrap' }}>
                        {colorPalettes.map((palette, index) => (
                          <button
                            key={palette.name}
                            onClick={() => setActiveColorPalette(index)}
                            style={{
                              padding: '6px 10px',
                              fontSize: '10px',
                              borderRadius: '8px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '700',
                              background: activeColorPalette === index 
                                ? theme.accent 
                                : '#f0f0f0',
                              color: activeColorPalette === index ? 'white' : '#666'
                            }}
                          >
                            {palette.name}
                          </button>
                        ))}
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '15px' }}>
                        {colorPalettes[activeColorPalette].colors.map(color => (
                          <button
                            key={color}
                            onClick={() => handleColorChange(color)}
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '8px',
                              border: `3px solid ${selectedColor === color ? '#333' : '#ddd'}`,
                              background: color,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              transform: selectedColor === color ? 'scale(1.1)' : 'scale(1)'
                            }}
                            title={color}
                          />
                        ))}
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="color"
                          value={selectedColor}
                          onChange={(e) => handleColorChange(e.target.value)}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            border: '2px solid #ddd',
                            cursor: 'pointer'
                          }}
                        />
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>Custom</span>
                      </div>
                    </div>
                  )}

                  {/* Effects */}
                  {activeToolTab === 'effects' && (
                    <div>
                      <h3 style={{
                        margin: '0 0 20px 0',
                        fontSize: '1.2rem',
                        fontWeight: '800',
                        background: `linear-gradient(45deg, ${theme.blue}, ${theme.pink})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        ✨ Effects & Filters
                      </h3>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                        {[
                          { name: 'Sketch Style', icon: '✏️', description: 'Hand-drawn look' },
                          { name: 'Halftone', icon: '📰', description: 'Comic book dots' },
                          { name: 'Pop Art', icon: '💥', description: 'Bold colors' },
                          { name: 'Vintage', icon: '📸', description: 'Aged paper effect' }
                        ].map(effect => (
                          <button
                            key={effect.name}
                            style={{
                              padding: '16px',
                              borderRadius: '12px',
                              border: '3px solid #ddd',
                              background: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = theme.blue;
                              e.currentTarget.style.background = `${theme.blue}05`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#ddd';
                              e.currentTarget.style.background = 'white';
                            }}
                          >
                            <div style={{ fontSize: '24px' }}>{effect.icon}</div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#333' }}>{effect.name}</div>
                              <div style={{ fontSize: '12px', color: '#666' }}>{effect.description}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {leftPanelCollapsed && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '24px',
                  marginBottom: '10px',
                  color: theme.primary
                }}>
                  🛠️
                </div>
                <div style={{
                  writingMode: 'vertical-rl',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: theme.primary
                }}>
                  Tools
                </div>
              </div>
            )}
          </div>
        )}

        {/* Center Panel - Canvas */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)',
          border: `3px solid ${theme.secondary}`,
          overflow: 'hidden'
        }}>
          {/* Mobile Tool Bar */}
          {isMobile && (
            <div style={{
              display: 'flex',
              gap: '10px',
              padding: '15px',
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
              color: 'white'
            }}>
              <button
                onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '700',
                  flex: 1
                }}
              >
                {leftPanelCollapsed ? '🛠️ Tools' : '✕ Hide Tools'}
              </button>
              <button
                onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '700',
                  flex: 1
                }}
              >
                {rightPanelCollapsed ? '📄 Pages' : '✕ Hide Pages'}
              </button>
            </div>
          )}

          {/* Canvas Header */}
          <div style={{
            background: `${theme.secondary}10`,
            padding: '20px',
            borderBottom: `2px solid ${theme.secondary}30`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <div style={{
                background: `linear-gradient(135deg, ${theme.secondary}, ${theme.primary})`,
                color: 'white',
                padding: '8px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '700'
              }}>
                Page {currentPageIndex + 1} of {pages.length}
              </div>
              
              <div style={{
                background: currentMode === TOOL_MODES.SELECT ? `${theme.blue}20` : `${theme.primary}20`,
                color: currentMode === TOOL_MODES.SELECT ? theme.blue : theme.primary,
                padding: '8px 12px',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: '700',
                border: `2px solid ${currentMode === TOOL_MODES.SELECT ? theme.blue : theme.primary}30`
              }}>
                {currentMode === TOOL_MODES.SELECT && '🔍 Select Mode'}
                {currentMode === TOOL_MODES.DRAW && '🖌️ Draw Mode'}
                {currentMode === TOOL_MODES.ERASER && '🧽 Erase Mode'}
                {currentMode === TOOL_MODES.BUBBLE && '💬 Speech Mode'}
                {currentMode === TOOL_MODES.TEXT && '🔤 Text Mode'}
                {currentMode === TOOL_MODES.LINE && '📏 Line Mode'}
                {currentMode === TOOL_MODES.RECTANGLE && '⬜ Rectangle Mode'}
                {currentMode === TOOL_MODES.CIRCLE && '⭕ Circle Mode'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleUndo}
                disabled={!hasUndo}
                style={{
                  background: hasUndo ? `${theme.success}20` : '#f0f0f0',
                  color: hasUndo ? theme.success : '#ccc',
                  border: `2px solid ${hasUndo ? theme.success : '#ddd'}`,
                  borderRadius: '10px',
                  padding: '8px 12px',
                  cursor: hasUndo ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                  fontWeight: '700'
                }}
              >
                ↶ Undo
              </button>
              <button
                onClick={handleRedo}
                disabled={!hasRedo}
                style={{
                  background: hasRedo ? `${theme.accent}20` : '#f0f0f0',
                  color: hasRedo ? theme.accent : '#ccc',
                  border: `2px solid ${hasRedo ? theme.accent : '#ddd'}`,
                  borderRadius: '10px',
                  padding: '8px 12px',
                  cursor: hasRedo ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                  fontWeight: '700'
                }}
              >
                ↷ Redo
              </button>
            </div>
          </div>

          {/* Canvas Area */}
          <div style={{
            flex: 1,
            position: 'relative',
            background: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '100%',
              maxWidth: '800px',
              height: '100%',
              minHeight: '400px',
              background: 'white',
              borderRadius: '15px',
              boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              border: `3px solid ${theme.primary}30`,
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Your actual DrawingCanvas and BubbleCanvas components */}
              <DrawingCanvas />
              <BubbleCanvas />

              {/* Canvas overlay with mode indicator */}
              <div style={{
                position: 'absolute',
                top: '15px',
                left: '15px',
                background: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                Current tool: {currentMode.charAt(0).toUpperCase() + currentMode.slice(1)}
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Pages */}
        {(!isMobile || !rightPanelCollapsed) && (
          <div style={{
            width: isMobile ? '100%' : rightPanelCollapsed ? '60px' : '280px',
            background: 'white',
            borderRadius: '20px',
            padding: rightPanelCollapsed ? '20px 15px' : '25px',
            boxShadow: '0 12px 35px rgba(0,0,0,0.1)',
            border: `3px solid ${theme.success}`,
            overflowY: 'auto',
            transition: 'width 0.3s ease',
            position: 'relative'
          }}>
            <button
              onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
              style={{
                position: 'absolute',
                top: '20px',
                left: '15px',
                background: theme.success,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                zIndex: 10,
                display: isMobile ? 'none' : 'block'
              }}
            >
              {rightPanelCollapsed ? '←' : '→'}
            </button>

            {!rightPanelCollapsed && (
              <>
                <h3 style={{
                  margin: '0 0 20px 0',
                  fontSize: '1.2rem',
                  fontWeight: '800',
                  background: `linear-gradient(45deg, ${theme.success}, ${theme.blue})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  📄 Pages & Panels
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                  {pages.map((page, index) => (
                    <div
                      key={page.id}
                      onClick={() => handleNavigateToPage(index)}
                      style={{
                        position: 'relative',
                        padding: '12px',
                        borderRadius: '12px',
                        border: `3px solid ${currentPageIndex === index ? theme.success : '#ddd'}`,
                        background: currentPageIndex === index 
                          ? `${theme.success}10` 
                          : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (currentPageIndex !== index) {
                          e.currentTarget.style.borderColor = theme.success;
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentPageIndex !== index) {
                          e.currentTarget.style.borderColor = '#ddd';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      <div 
                        style={{
                          width: '100%',
                          height: '80px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          background: '#f8f9fa',
                          position: 'relative',
                          overflow: 'hidden',
                          marginBottom: '8px'
                        }}
                      >
                        {/* Page preview placeholder */}
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          opacity: 0.3
                        }}>
                          📄
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#333'
                        }}>
                          Page {index + 1}
                        </div>
                        
                        {pages.length > 1 && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Delete this page?')) {
                                handleDeletePage(index);
                              }
                            }}
                            style={{
                              background: '#ffebee',
                              color: '#d32f2f',
                              border: '2px solid #ffcdd2',
                              borderRadius: '6px',
                              padding: '4px 6px',
                              cursor: 'pointer',
                              fontSize: '10px'
                            }}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <button 
                    onClick={handleAddNewPage}
                    style={{
                      width: '100%',
                      padding: '20px',
                      border: '3px dashed #ddd',
                      borderRadius: '12px',
                      background: 'white',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = theme.success;
                      e.currentTarget.style.background = `${theme.success}05`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#ddd';
                      e.currentTarget.style.background = 'white';
                    }}
                  >
                    <div style={{ fontSize: '24px', color: '#ccc', marginBottom: '8px' }}>+</div>
                    <div style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Add New Page</div>
                  </button>
                </div>

                {/* Quick Actions */}
                <div>
                  <h4 style={{
                    margin: '0 0 15px 0',
                    fontSize: '1rem',
                    fontWeight: '700',
                    color: theme.accent
                  }}>
                    ⚡ Quick Actions
                  </h4>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button
                      onClick={() => {
                        // Quick add speech bubble
                        const newBubble = {
                          id: `bubble-${Date.now()}`,
                          x: 200,
                          y: 150,
                          width: 150,
                          height: 80,
                          text: "Speech bubble!",
                          type: toolContext.bubbleType,
                          fontSize: toolContext.fontSize,
                          fontFamily: toolContext.fontFamily
                        };
                        
                        const currentPage = pages[currentPageIndex];
                        const existingBubbles = currentPage?.bubbles || [];
                        
                        updatePage(currentPageIndex, {
                          bubbles: [...existingBubbles, newBubble]
                        });
                        
                        addToUndoStack({
                          type: 'ADD_BUBBLE',
                          pageIndex: currentPageIndex,
                          bubble: newBubble
                        });
                        
                        // Auto-select the new bubble
                        selectElement(newBubble.id, 'bubble');
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: `${theme.pink}10`,
                        color: theme.pink,
                        border: `2px solid ${theme.pink}30`,
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${theme.pink}20`;
                        e.currentTarget.style.borderColor = theme.pink;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `${theme.pink}10`;
                        e.currentTarget.style.borderColor = `${theme.pink}30`;
                      }}
                    >
                      <span>💬</span>
                      <span>Quick Speech Bubble</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        // Add action effect elements (POW, BAM, etc.)
                        const effects = [
                          { text: "POW!", x: 150, y: 100, color: '#FF0000' },
                          { text: "BAM!", x: 350, y: 200, color: '#FF6600' },
                          { text: "BOOM!", x: 250, y: 300, color: '#FF0066' }
                        ];
                        
                        const randomEffect = effects[Math.floor(Math.random() * effects.length)];
                        
                        const actionBubble = {
                          id: `action-${Date.now()}`,
                          x: randomEffect.x,
                          y: randomEffect.y,
                          width: 120,
                          height: 60,
                          text: randomEffect.text,
                          type: 'shout',
                          fontSize: 24,
                          fontFamily: 'Impact, sans-serif'
                        };
                        
                        const currentPage = pages[currentPageIndex];
                        const existingBubbles = currentPage?.bubbles || [];
                        
                        updatePage(currentPageIndex, {
                          bubbles: [...existingBubbles, actionBubble]
                        });
                        
                        addToUndoStack({
                          type: 'ADD_ACTION_EFFECT',
                          pageIndex: currentPageIndex,
                          bubble: actionBubble
                        });
                        
                        selectElement(actionBubble.id, 'bubble');
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: `${theme.accent}10`,
                        color: theme.accent,
                        border: `2px solid ${theme.accent}30`,
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${theme.accent}20`;
                        e.currentTarget.style.borderColor = theme.accent;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `${theme.accent}10`;
                        e.currentTarget.style.borderColor = `${theme.accent}30`;
                      }}
                    >
                      <span>💥</span>
                      <span>Action Effects</span>
                    </button>
                  </div>
                </div>
              </>
            )}

            {rightPanelCollapsed && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '24px',
                  marginBottom: '10px',
                  color: theme.success
                }}>
                  📄
                </div>
                <div style={{
                  writingMode: 'vertical-rl',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: theme.success
                }}>
                  Pages
                </div>
              </div>
            )}
          </div>
        )}
      </div>



      {/* Share Story Modal */}
      {showShareModal && (
        <ShareStoryModal
          projectName={projectName}
          author={author}
          pages={pages}
          onClose={() => setShowShareModal(false)}
          onShare={(data) => {
            console.log('Share story:', data);
            setShowShareModal(false);
          }}
          onPublishToGallery={(data) => {
            console.log('Publish to gallery:', data);
            setShowShareModal(false);
          }}
        />
      )}
    </div>
  );
};

export default SketchedCanvasInterface;